// TODO: remove this file if this file replaces the default ImageCropper
/* istanbul ignore file */
import { useCallback } from 'react';
import type React from 'react';

import type Cropper from 'cropperjs';

import { Button, Text } from '@change/ds-core-ui';
import { CloseIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { MediaCropArea } from '#src/app/shared/hooks/imageUpload';
import type { UploaderControls } from '#src/app/shared/hooks/uppyUploader';

import { CropperElement } from './CropperElement';

type ImageCropperProps = {
	crop?: MediaCropArea;
	disabled?: boolean;
	image?: string;
	onCancelClick?: () => void;
	onImageChange: (image?: string) => void;
	onImageCrop: (imageCrop?: MediaCropArea) => void;
	uploader: UploaderControls;
	onInitialized?: (instance: Cropper) => void;
};

export const ImageCropper: React.FC<ImageCropperProps> = ({
	crop,
	disabled = false,
	image,
	onImageChange,
	onCancelClick,
	onImageCrop,
	uploader,
	onInitialized,
}) => {
	const { translate } = useI18n();
	const onCancelReposition = useCallback(() => {
		const file = uploader.getFile();
		if (file) {
			uploader.removeFile(file.id);
		}
		onImageChange(undefined);
		onImageCrop(undefined);
		if (onCancelClick) onCancelClick();
	}, [onCancelClick, onImageChange, onImageCrop, uploader]);

	return (
		<div className="image-cropper">
			<div className="relative aspect-video [overflow:inherit]">
				<CropperElement
					crop={crop}
					data-testid="cropper-element"
					disabled={disabled}
					image={image}
					updateCrop={onImageCrop}
					updateImage={uploader.updateImage}
					onInitialized={onInitialized}
				/>
				{!disabled && (
					<Button
						variant="secondary-solid"
						size="small"
						iconOnly
						// Button clones leadingIcon and force-overrides its `size` prop to "medium",
						// so shrinking the icon has to go through className/svgClassName instead.
						leadingIcon={<CloseIcon className="h-4 w-4" svgClassName="h-4 w-4" />}
						aria-label={translate('corgi.sap.media.a11y.cropper.button_aria_label')}
						data-testid="remove-img-btn"
						className="absolute top-0 right-0 z-[1] mt-4 mr-4"
						onClick={onCancelReposition}
					/>
				)}
			</div>
			{!disabled && (
				<div
					data-testid="cropper-instructions"
					className="mt-0 rounded-none border border-solid border-[#999999] bg-[#F2F2F2] p-2"
				>
					<Text size="small">
						<Translate value="corgi.sap.media.cropper.reposition" />
					</Text>
				</div>
			)}
		</div>
	);
};
